/**
 * RacesPage — listado de race_targets del atleta (v15.5-§8.3.C+D+E).
 *
 * §8.3.C: lectura + entry point desde PlanPage.
 * §8.3.D: botón "+ Añadir carrera" (header + empty state CTA),
 *         acciones Editar/Borrar por race_target, modales asociados.
 * §8.3.E: acción "Marcar como principal" → PromoteRaceModal + feedback
 *         post-acción (toast + banner CTA "Ver plan nuevo →" persistente
 *         hasta clic/dismiss).
 *
 * Cap 5 race_targets por user (invariante backend §8.3): el botón Add
 * se deshabilita visualmente cuando se alcanza el cap, con texto
 * explicativo. El backend también valida (defensa en profundidad).
 *
 * Patrón fetch/state idéntico a PlanPage. Tras cada CUD → refresh
 * (refetch GET /races, no append/remove local — más simple y siempre
 * consistente con servidor).
 *
 * Sistema toast: local a esta página (mismo patrón que PlanPage; no hay
 * provider global en el proyecto). Auto-dismiss 5s. El banner del
 * promote success NO es un toast — es persistente con CTA que navega
 * a /plan; vive hasta clic/dismiss manual.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { getOnboardingStatus } from "@/api/onboarding";
import { getRaces } from "@/api/races";
import { DeleteRaceModal } from "@/components/races/DeleteRaceModal";
import { PromoteRaceModal } from "@/components/races/PromoteRaceModal";
import { RaceResultModal } from "@/components/races/RaceResultModal";
import { RaceCard } from "@/components/races/RaceCard";
import { RaceTargetModal } from "@/components/races/RaceTargetModal";
import { ResultsTabContent } from "@/pages/Races/components/ResultsTabContent";
import { t } from "@/i18n/es";
import type {
  AthleteLevel,
  PromoteRaceResponse,
  RaceTarget,
} from "@/types/api";

type ActiveTab = "races" | "results";

const MAX_RACE_TARGETS_PER_USER = 5;

interface Toast {
  id: number;
  message: string;
  kind: "success" | "error" | "info";
}

let toastSeq = 0;

export function RacesPage() {
  const [races, setRaces] = useState<RaceTarget[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // §8.3.D — state de modales abiertos.
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState<RaceTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RaceTarget | null>(null);
  // §8.3.E — modal promote + feedback post-acción.
  const [promoteTarget, setPromoteTarget] = useState<RaceTarget | null>(null);
  // v16.3 B.4: modal de "Añadir resultado" — abierto desde RaceCard cuando
  // race ya pasada + sin resultado registrado todavía.
  const [resultTarget, setResultTarget] = useState<RaceTarget | null>(null);
  // Banner CTA persistente que aparece tras un promote con regen exitosa.
  // NO se auto-dismiss — vive hasta que el atleta hace clic en CTA o
  // dismiss. Diferencia clave con el toast (que sí se auto-dismiss en 5s).
  const [showPlanBanner, setShowPlanBanner] = useState(false);
  // Sistema toast (patrón PlanPage copy-paste).
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Nivel del atleta para el filtro §8.4 de distance options.
  const [athleteLevel, setAthleteLevel] = useState<AthleteLevel | null>(null);
  // v16.3.1 R.1 + v16.4.1 §T4: pestaña activa de la sección. Default
  // "races" (vista histórica) — la pestaña Resultados es secundaria y
  // se activa por click del atleta. Sin URL routing (state local).
  const [activeTab, setActiveTab] = useState<ActiveTab>("races");

  const showToast = useCallback(
    (message: string, kind: Toast["kind"] = "success") => {
      const id = ++toastSeq;
      setToasts((prev) => [...prev, { id, message, kind }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((tt) => tt.id !== id));
      }, 5000);
    },
    [],
  );

  async function fetchRaces() {
    setLoading(true);
    setError(null);
    try {
      const result = await getRaces();
      setRaces(result.races);
    } catch {
      setError(t.races.errorLoad);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Paralelo: races + level del onboarding.
      try {
        const result = await getRaces();
        if (!cancelled) {
          setRaces(result.races);
        }
      } catch {
        if (!cancelled) {
          setError(t.races.errorLoad);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    void (async () => {
      // Fetch del level — fail-safe (si falla, level queda null y el
      // filtro de distance no aplica restricción — backend lo cubrirá).
      try {
        const status = await getOnboardingStatus();
        if (!cancelled) {
          setAthleteLevel(status.profile?.level ?? null);
        }
      } catch {
        // ignore — defensa.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const racesCount = races?.length ?? 0;
  const isAtCap = racesCount >= MAX_RACE_TARGETS_PER_USER;

  // §8.3.E: para el modal de promote necesitamos saber cuál es la primary
  // actual (el copy es "vas a cambiar de X a Y"). Null si no hay primary
  // (raro pero posible).
  const currentPrimary = races?.find((r) => r.is_primary) ?? null;

  function openAddModal() {
    setShowAddModal(true);
  }

  function handleEdit(id: number) {
    const target = races?.find((r) => r.id === id);
    if (target) setEditTarget(target);
  }

  function handleDelete(id: number) {
    const target = races?.find((r) => r.id === id);
    if (target) setDeleteTarget(target);
  }

  function handlePromote(id: number) {
    const target = races?.find((r) => r.id === id);
    if (target) setPromoteTarget(target);
  }

  function handleAddResult(id: number) {
    const target = races?.find((r) => r.id === id);
    if (target) setResultTarget(target);
  }

  /** Handler de éxito del promote. Tres ramas según response del backend:
   *  - was_already_primary=true → toast info silencioso, NO banner CTA.
   *  - plan_regenerated=true → toast success + banner CTA persistente.
   *  - plan_regenerated=false (sin plan activo) → toast success diferente,
   *    NO banner CTA (no hay plan que mostrar).
   *  Siempre: refresh lista (el primary cambió, hay que sincronizar). */
  function handlePromoted(response: PromoteRaceResponse) {
    if (response.was_already_primary) {
      showToast(t.races.promoteToastAlreadyPrimary, "info");
      // NO banner — no hubo regen.
    } else if (response.plan_regenerated) {
      showToast(t.races.promoteToastSuccessWithRegen, "success");
      setShowPlanBanner(true);
    } else {
      // Swap aplicado, pero user sin plan activo.
      showToast(t.races.promoteToastSuccessNoPlan, "success");
      // NO banner — no hay plan nuevo que mostrar.
    }
    void fetchRaces();
  }

  // Toast stack reutilizable (patrón PlanPage). aria-live="polite" para
  // que screen readers lo anuncien sin interrumpir.
  const toastStack = toasts.length > 0 ? (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-toast right-4 z-50 flex max-w-xs flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          data-testid={`toast-${toast.kind}`}
          className="pointer-events-auto rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone"
        >
          {toast.message}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <>
      <main className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone">
        <header>
          <Link
            to="/plan"
            data-testid="races-back-link"
            className="text-xs text-bone-muted hover:text-bone"
          >
            {t.races.backToPlan}
          </Link>
          <p className="mt-4 text-xs uppercase tracking-widest text-gray-mid">
            {t.races.pageTagline}
          </p>
          <h1 className="mt-2 text-2xl font-medium tracking-tight">
            {t.races.pageTitle}
          </h1>
          <p className="mt-2 text-sm text-bone-muted">{t.races.subtitle}</p>

          {/* §8.3.D: botón "+ Añadir carrera" en header. Visible siempre
              cuando hay races (cuando empty, el botón vive en el empty
              state — más prominente). Deshabilitado si cap=5 alcanzado. */}
          {!loading && races !== null && races.length > 0 && (
            <div className="mt-4 flex flex-col items-start gap-1">
              <button
                type="button"
                data-testid="races-add-button"
                onClick={openAddModal}
                disabled={isAtCap}
                className="rounded-md border border-border-subtle bg-carbon-raised px-4 py-2 text-sm text-bone transition-colors duration-fast hover:border-border-strong disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.races.addCta}
              </button>
              {isAtCap && (
                <p
                  data-testid="races-cap-reached"
                  className="text-xs text-bone-muted"
                >
                  {t.races.capReached}
                </p>
              )}
            </div>
          )}
        </header>

        {/* §8.3.E: banner persistente post-promote con CTA al plan nuevo.
            Aparece SOLO tras un promote con plan_regenerated=true. Vive
            hasta clic/dismiss. NO se auto-dismiss (a diferencia del
            toast paralelo que sí desaparece a los 5s). */}
        {showPlanBanner && (
          <section
            data-testid="promote-success-banner"
            role="status"
            className="flex items-start justify-between gap-3 rounded-md border border-lime/40 bg-lime/10 p-3"
          >
            <div className="flex flex-col gap-2">
              <p className="text-sm text-bone">{t.races.promoteBannerText}</p>
              <Link
                to="/plan"
                data-testid="promote-success-banner-cta"
                className="text-sm font-medium text-lime hover:underline"
              >
                {t.races.promoteBannerCta}
              </Link>
            </div>
            <button
              type="button"
              data-testid="promote-success-banner-dismiss"
              onClick={() => setShowPlanBanner(false)}
              aria-label={t.races.promoteBannerDismiss}
              className="text-bone-muted hover:text-bone"
            >
              ×
            </button>
          </section>
        )}

        {loading && (
          <p
            data-testid="races-loading"
            className="text-sm text-bone-muted"
          >
            {t.races.loading}
          </p>
        )}

        {!loading && error !== null && (
          <p
            data-testid="races-error"
            role="alert"
            className="text-sm text-danger"
          >
            {error}
          </p>
        )}

        {/* v16.3.1 R.1 + v16.4.1 §T4: tabs solo si hay races (ni en empty
            state ni en loading). El segundo punto de entrada al formulario
            de resultado vive bajo la pestaña "Resultados". */}
        {!loading && error === null && races !== null && races.length > 0 && (
          <div
            role="tablist"
            aria-label={t.races.pageTitle}
            data-testid="races-tablist"
            className="flex gap-2 border-b border-border-subtle"
          >
            <button
              type="button"
              role="tab"
              id="races-tab-races"
              aria-selected={activeTab === "races"}
              aria-controls="races-tabpanel-races"
              data-testid="races-tab-races"
              onClick={() => setActiveTab("races")}
              className={`px-3 py-2 text-sm transition-colors duration-fast ${
                activeTab === "races"
                  ? "border-b-2 border-lime text-bone"
                  : "text-bone-muted hover:text-bone"
              }`}
            >
              {t.races.tabsLabelRaces}
            </button>
            <button
              type="button"
              role="tab"
              id="races-tab-results"
              aria-selected={activeTab === "results"}
              aria-controls="races-tabpanel-results"
              data-testid="races-tab-results"
              onClick={() => setActiveTab("results")}
              className={`px-3 py-2 text-sm transition-colors duration-fast ${
                activeTab === "results"
                  ? "border-b-2 border-lime text-bone"
                  : "text-bone-muted hover:text-bone"
              }`}
            >
              {t.races.tabsLabelResults}
            </button>
          </div>
        )}

        {/* Empty state: mensaje + CTA centrado. §8.3.D evoluciona el
            empty del §8.3.C (que era solo texto). */}
        {!loading && error === null && races !== null && races.length === 0 && (
          <section
            data-testid="races-empty"
            className="flex flex-col items-center gap-4 py-8 text-center"
          >
            <p className="text-sm text-bone-muted">{t.races.emptyText}</p>
            <button
              type="button"
              data-testid="races-empty-add-button"
              onClick={openAddModal}
              className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite hover:bg-lime/90"
            >
              {t.races.addCta}
            </button>
          </section>
        )}

        {!loading
          && error === null
          && races !== null
          && races.length > 0
          && activeTab === "races" && (
          <section
            data-testid="races-list"
            id="races-tabpanel-races"
            role="tabpanel"
            aria-labelledby="races-tab-races"
            className="flex flex-col gap-3"
          >
            {races.map((race) => (
              <RaceCard
                key={race.id}
                race={race}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onPromote={handlePromote}
                onAddResult={handleAddResult}
              />
            ))}
          </section>
        )}

        {!loading
          && error === null
          && races !== null
          && races.length > 0
          && activeTab === "results" && (
          <section
            id="races-tabpanel-results"
            role="tabpanel"
            aria-labelledby="races-tab-results"
          >
            <ResultsTabContent races={races} onAddResult={handleAddResult} />
          </section>
        )}

        {/* §8.3.D + E: modales condicionales. */}
        {showAddModal && (
          <RaceTargetModal
            mode="create"
            athleteLevel={athleteLevel}
            onClose={() => setShowAddModal(false)}
            onSaved={() => void fetchRaces()}
          />
        )}
        {editTarget && (
          <RaceTargetModal
            mode="edit"
            race={editTarget}
            athleteLevel={athleteLevel}
            onClose={() => setEditTarget(null)}
            onSaved={() => void fetchRaces()}
          />
        )}
        {deleteTarget && (
          <DeleteRaceModal
            race={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onDeleted={() => void fetchRaces()}
          />
        )}
        {promoteTarget && (
          <PromoteRaceModal
            race={promoteTarget}
            currentPrimary={currentPrimary}
            onClose={() => setPromoteTarget(null)}
            onPromoted={handlePromoted}
            onStaleData={() => void fetchRaces()}
          />
        )}
        {/* v16.3 B.4: modal "Añadir resultado" abierto desde RaceCard. */}
        {resultTarget && (
          <RaceResultModal
            raceId={resultTarget.id}
            isOpen
            onClose={() => setResultTarget(null)}
            onSuccess={() => {
              setResultTarget(null);
              showToast(t.races.resultToastSuccess, "success");
              void fetchRaces();
            }}
          />
        )}
      </main>
      {toastStack}
    </>
  );
}
